import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Stack,
  alpha,
  useTheme,
  Button,
  Divider
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SearchIcon from '@mui/icons-material/Search';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SummarizeIcon from '@mui/icons-material/Summarize';

interface AnalysisPreviewProps {
  dataSources: string[];
  focusAreas: string[];
  selectedDocuments: string[];
  selectedMetrics: string[];
  financialDocuments: any[];
  hotelMetrics: any[];
  onRunAnalysis: () => void;
  analyzing: boolean;
  canRunAnalysis: boolean;
}

const AnalysisPreview: React.FC<AnalysisPreviewProps> = ({
  dataSources,
  focusAreas,
  selectedDocuments,
  selectedMetrics,
  financialDocuments,
  hotelMetrics,
  onRunAnalysis,
  analyzing,
  canRunAnalysis
}) => {
  const theme = useTheme();

  const dataSourceConfig = [
    {
      id: 'market',
      icon: SearchIcon,
      title: 'Market Research',
      color: theme.palette.primary.main
    },
    {
      id: 'financial',
      icon: DescriptionIcon,
      title: 'Financial Documents',
      color: theme.palette.info.main
    },
    {
      id: 'metrics',
      icon: TrendingUpIcon,
      title: 'Performance Metrics',
      color: theme.palette.success.main
    }
  ];

  const focusAreaConfig = [
    { id: 'financials', title: 'Financial Analysis', icon: '💰' },
    { id: 'market', title: 'Market Position', icon: '📊' },
    { id: 'renovation', title: 'Property Renovation', icon: '🔨' },
    { id: 'sustainability', title: 'Sustainability', icon: '🌱' },
    { id: 'marketing', title: 'Marketing Strategy', icon: '📢' }
  ];

  return (
    <Box sx={{
      p: 3,
      bgcolor: alpha(theme.palette.primary.main, 0.02),
      borderRadius: 2,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SummarizeIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Analysis Configuration Summary
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          onClick={onRunAnalysis}
          disabled={!canRunAnalysis || analyzing}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: theme.palette.primary.main,
            '&:hover': {
              bgcolor: theme.palette.primary.dark,
              transform: 'translateY(-1px)',
              boxShadow: theme.shadows[4]
            },
            '&:disabled': {
              bgcolor: theme.palette.grey[300],
              color: theme.palette.grey[500]
            }
          }}
        >
          {analyzing ? 'Analyzing...' : 'Run Analysis'}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Data Sources Section */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, fontSize: '0.95rem' }}>
            📊 Data Sources ({dataSources.length} selected)
          </Typography>

          {dataSources.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No data sources selected
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {dataSources.map(ds => {
                const config = dataSourceConfig.find(c => c.id === ds);
                if (!config) return null;
                return (
                  <Box key={ds} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      size="small"
                      label={config.title}
                      icon={<config.icon style={{ fontSize: 14 }} />}
                      sx={{
                        bgcolor: alpha(config.color, 0.1),
                        color: config.color,
                        fontSize: '0.75rem',
                        height: 24,
                        '& .MuiChip-icon': { fontSize: 14 }
                      }}
                    />
                    {ds === 'financial' && (
                      <Typography variant="caption" color="text.secondary">
                        {selectedDocuments.length} of {financialDocuments.length} documents
                      </Typography>
                    )}
                    {ds === 'metrics' && (
                      <Typography variant="caption" color="text.secondary">
                        {selectedMetrics.length} of {hotelMetrics.length} data points
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </Grid>

        {/* Focus Areas Section */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, fontSize: '0.95rem' }}>
            🎯 Focus Areas ({focusAreas.length} selected)
          </Typography>

          {focusAreas.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No focus areas selected
            </Typography>
          ) : (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {focusAreas.map(area => {
                const config = focusAreaConfig.find(c => c.id === area);
                if (!config) return null;
                return (
                  <Chip
                    key={area}
                    size="small"
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span style={{ fontSize: '0.7rem' }}>{config.icon}</span>
                        {config.title}
                      </Box>
                    }
                    color="primary"
                    variant="outlined"
                    sx={{
                      fontSize: '0.75rem',
                      height: 24,
                      mb: 0.5
                    }}
                  />
                );
              })}
            </Stack>
          )}
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Analysis Details */}
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, fontSize: '0.85rem' }}>
          Analysis Details
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">Total Data Points</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {dataSources.length + selectedDocuments.length + selectedMetrics.length}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">Analysis Scope</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {focusAreas.length === 0 ? 'General' : `${focusAreas.length} specialized areas`}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">Estimated Duration</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              2-5 minutes
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Warning if no configuration */}
      {(!canRunAnalysis || (dataSources.length === 0 && focusAreas.length === 0)) && (
        <Box sx={{
          mt: 2,
          p: 2,
          bgcolor: alpha(theme.palette.warning.main, 0.1),
          borderRadius: 1.5,
          border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
        }}>
          <Typography variant="body2" color="warning.main" sx={{ fontWeight: 500 }}>
            ⚠️ Please select at least one data source or focus area to run the analysis.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AnalysisPreview;